import { useState, useEffect, useCallback, type RefObject } from "react";
import type { MapRef } from "../components/MapCanvas";
import type { LatLng } from "../types";
import { isMappable } from "../utils/coords";

interface CameraViewState {
  longitude: number;
  latitude: number;
  zoom: number;
  pitch: number;
}

interface UseMapCameraOptions {
  mapRef: RefObject<MapRef | null>;
  center: { lat: number; lng: number };
  zoom: number;
  pitch?: number;
  paddingBottom?: number;
  highlightedId: string | null;
  /** Spaces only flow in (bounds math) — any LatLng-shaped array works. */
  spaces: readonly LatLng[];
}

/**
 * Owns the controlled MapGL view state and every camera move. Semantics kept
 * from the legacy map: a highlighted space flies to the given center; with
 * nothing highlighted and ≥2 mappable spaces, `fitBounds` computes whatever
 * zoom actually fits them (a fixed zoom can't guarantee all pins on screen);
 * otherwise fly to the center. Falls back to setting view state directly
 * while the map ref isn't available.
 */
export function useMapCamera({
  mapRef,
  center,
  zoom,
  pitch = 0,
  paddingBottom = 0,
  highlightedId,
  spaces,
}: UseMapCameraOptions): {
  viewState: CameraViewState;
  onMove: (evt: { viewState: CameraViewState }) => void;
  flyTo: (
    lng: number,
    lat: number,
    z: number,
    p: number,
    padBottom: number,
    durationMs?: number,
  ) => void;
  recenter: () => void;
} {
  const [viewState, setViewState] = useState<CameraViewState>({
    longitude: center.lng,
    latitude: center.lat,
    zoom,
    pitch,
  });

  const flyTo = useCallback(
    (
      lng: number,
      lat: number,
      z: number,
      p: number,
      padBottom: number,
      durationMs = 1200,
    ) => {
      const map = mapRef.current;
      if (map) {
        map.flyTo({
          center: [lng, lat],
          zoom: z,
          pitch: p,
          padding: { top: 0, bottom: padBottom, left: 0, right: 0 },
          duration: durationMs,
          essential: true,
        });
      } else {
        setViewState((prev) => ({
          ...prev,
          longitude: lng,
          latitude: lat,
          zoom: z,
          pitch: p,
        }));
      }
    },
    [mapRef],
  );

  const fitAllSpaces = useCallback(
    (padBottom: number) => {
      const map = mapRef.current;
      const valid = spaces.filter(isMappable);
      if (!map || valid.length < 2) return false;
      let minLat = valid[0].lat;
      let maxLat = valid[0].lat;
      let minLng = valid[0].lng;
      let maxLng = valid[0].lng;
      valid.forEach((s) => {
        minLat = Math.min(minLat, s.lat);
        maxLat = Math.max(maxLat, s.lat);
        minLng = Math.min(minLng, s.lng);
        maxLng = Math.max(maxLng, s.lng);
      });
      map.fitBounds(
        [
          [minLng, minLat],
          [maxLng, maxLat],
        ],
        {
          padding: {
            top: 60,
            bottom: 60 + padBottom,
            left: 48,
            right: 48,
          },
          duration: 1200,
          maxZoom: 15,
        },
      );
      return true;
    },
    [mapRef, spaces],
  );

  useEffect(() => {
    if (!highlightedId && fitAllSpaces(paddingBottom)) return;
    flyTo(center.lng, center.lat, zoom, pitch, paddingBottom);
  }, [
    highlightedId,
    fitAllSpaces,
    center.lat,
    center.lng,
    zoom,
    pitch,
    paddingBottom,
    flyTo,
  ]);

  const onMove = useCallback(
    (evt: { viewState: CameraViewState }) => setViewState(evt.viewState),
    [],
  );

  const recenter = useCallback(
    () => flyTo(center.lng, center.lat, zoom, pitch, paddingBottom),
    [flyTo, center.lng, center.lat, zoom, pitch, paddingBottom],
  );

  return { viewState, onMove, flyTo, recenter };
}
